/**
 * 원인행위 공통 워크스페이스
 * - register: 작성·저장·확정 또는 승인요청 (기관 obligationConfirmMode)
 * - approve: APPROVE 모드일 때만 승인/반려
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';
import useScrollbarHoverProps from 'hooks/useScrollbarHoverProps';
import {
  FormArea,
  CollapsibleSearchPanel,
  CollapsibleSectionPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import { loadEnvSettings } from 'system/envSettingsStore';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import {
  getExpenseLoadEnv,
  buildLoadDialogTabs,
  loadExpenseBalanceRows,
  BALANCE_SEARCH_CONFIG,
  applyBalanceFields,
  confirmUpstreamDupIfNeeded,
} from 'expense/expenseLoadUtils';
import {
  EXPENSE_STATUS,
  EXPENSE_STATUS_OPTIONS,
  OBLIGATION_TYPE_OPTIONS,
  OBLIGATION_CONFIRM_MODE,
  obligationTypeLabel,
  expenseStatusLabel,
  createObligationUid,
  createObligationDetailUid,
  createContractUid,
  emptyObligationMaster,
  emptyObligationVendorRow,
  emptyContractRow,
  loadObligationBundle,
  saveObligationBundle,
  loadProposalBundle,
  nextObligationNo,
  filterObligationsForRegister,
  filterObligationsForApprove,
  confirmObligation,
  requestObligationApproval,
  approveObligation,
  rejectObligation,
  todayYmd,
} from 'expense/expenseStore';
import {
  ensureContractSeed,
  filterContracts,
  enrichContractRow,
  loadContractBundle,
  createContractsFromObligation,
  linkContractsToObligation,
} from 'contract/contractStore';

const VENDOR_GRID_ID = 'expense-obligation-vendor';
const CONTRACT_GRID_ID = 'expense-obligation-contract';
const LIST_GRID_ID = 'expense-obligation-list';

// 영역별 높이 — 펼침 시 서로 줄이지 않고 아래로 늘어나므로 각자 높이를 갖는다
const VENDOR_PANEL_H = '300px'; // 최소 높이(남는 공간이 있으면 더 늘어남)
const CONTRACT_PANEL_H = '300px';
const LIST_PANEL_H = '340px';

/**
 * 세로 흐름 — 각 영역은 자기 높이를 갖고, 계약·목록을 펼치면 위 영역을 줄이지 않고
 * 아래로 늘어나며 화면이 스크롤된다 (형제 간 높이 분배 아님).
 */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${hoverExpandScrollbarCss}

  > * {
    flex-shrink: 0;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || '0 0 auto'};
  height: ${(p) => p.$height || 'auto'};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

/** 버튼 유무와 무관하게 높이 고정 — 추가/선택 시 그리드 영역이 밀리지 않도록 */
const MasterToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  min-height: 34px;
  box-sizing: border-box;
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const BalanceBtn = styled.button`
  appearance: none;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.xs};
  padding: 3px 8px;
  min-height: 26px;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  &:disabled {
    background: ${theme.colors.background.light};
    color: ${theme.colors.text.secondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

/**
 * 열 슬롯 폭 — 행이 달라도 각 열의 「시작점」을 맞춤 (품의등록과 동일)
 * 입력 폭(width)은 값 길이에 맞춰 슬롯보다 짧게 둘 수 있음
 */
const SEARCH_SLOT_W = ['230px', '280px', '330px'];
const SEARCH_DATE_W = '116px';

const MASTER_SLOT_W = ['210px', '230px', '290px', '290px', '200px', '200px'];
/** 적요는 재원+품의번호(짧은 2개 열) 자리를 합쳐 사용 */
const MASTER_SLOT_W_REMARK = '412px';
const MASTER_LABEL_W = '64px';

function emptySearch(partial = {}) {
  return {
    searchFiscalYear: getCurrentFiscalYear(),
    searchStatus: '',
    searchDeptCode: '',
    searchDeptName: '',
    searchDeptDisp: '',
    searchProjectCode: '',
    searchProjectName: '',
    searchProjectDisp: '',
    searchSubjectCode: '',
    searchSubjectName: '',
    searchSubjectDisp: '',
    searchDateFrom: '',
    searchDateTo: '',
    searchRemark: '',
    ...partial,
  };
}

function summarizeSelection(names = []) {
  const list = (names || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  return `${list[0]} 외 ${list.length - 1}건`;
}

function selectionTooltip(joinedNames, emptyText) {
  const list = String(joinedNames || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!list.length) return emptyText;
  if (list.length === 1) return list[0];
  return `선택 ${list.length}건\n${list.map((n) => `· ${n}`).join('\n')}`;
}

/** 조회조건 키 — 마스터 선택/reset 시 덮어쓰지 않음 */
const SEARCH_FIELD_KEYS = [
  'searchFiscalYear',
  'searchStatus',
  'searchDeptCode',
  'searchDeptName',
  'searchDeptDisp',
  'searchProjectCode',
  'searchProjectName',
  'searchProjectDisp',
  'searchSubjectCode',
  'searchSubjectName',
  'searchSubjectDisp',
  'searchDateFrom',
  'searchDateTo',
  'searchRemark',
];

function pickSearchValues(values = {}) {
  const out = {};
  SEARCH_FIELD_KEYS.forEach((k) => {
    out[k] = values[k] ?? '';
  });
  return out;
}

function emptyMasterForm(partial = {}) {
  return emptyObligationMaster({
    obligationDate: todayYmd(),
    ...partial,
  });
}

/**
 * @param {{ screenMode?: 'register'|'approve' }} props
 */
function ExpenseObligationWorkspaceInner({ screenMode = 'register' }) {
  const isApprove = screenMode === 'approve';
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const scrollbarHoverProps = useScrollbarHoverProps();

  const confirmMode = useMemo(() => {
    const env = loadEnvSettings(tenantCode);
    return env?.expense?.obligationConfirmMode === OBLIGATION_CONFIRM_MODE.APPROVE
      ? OBLIGATION_CONFIRM_MODE.APPROVE
      : OBLIGATION_CONFIRM_MODE.CONFIRM;
  }, [tenantCode]);

  const isConfirmMode = confirmMode === OBLIGATION_CONFIRM_MODE.CONFIRM;

  const searchDefaults = useMemo(
    () =>
      emptySearch({
        // 등록: 전체 · 승인(APPROVE): 승인요청 · 승인(CONFIRM 조회): 확정
        searchStatus: isApprove
          ? isConfirmMode
            ? EXPENSE_STATUS.CONFIRMED
            : EXPENSE_STATUS.REQUESTED
          : '',
      }),
    [isApprove, isConfirmMode]
  );

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...searchDefaults,
      ...emptyMasterForm({ fiscalYear: searchDefaults.searchFiscalYear }),
    },
  });
  const { getValues, reset, setValue, control } = methods;

  const vendorRef = useRef(null);
  const contractRef = useRef(null);
  const listRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [bundleVendors, setBundleVendors] = useState([]);
  const [bundleContracts, setBundleContracts] = useState([]);
  const [balanceLookupOpen, setBalanceLookupOpen] = useState(false);
  const [searchLookup, setSearchLookup] = useState({ open: false, type: '' });
  const [listCount, setListCount] = useState(0);
  const [contractCount, setContractCount] = useState(0);
  const [contractLookupOpen, setContractLookupOpen] = useState(false);
  const [contractLookupRows, setContractLookupRows] = useState([]);

  const masterStatus = useWatch({ control, name: 'status' });
  const searchFiscalYearWatch = useWatch({ control, name: 'searchFiscalYear' });
  const searchDeptNameWatch = useWatch({ control, name: 'searchDeptName' });
  const searchProjectNameWatch = useWatch({ control, name: 'searchProjectName' });
  const searchSubjectNameWatch = useWatch({ control, name: 'searchSubjectName' });
  const masterReadonly =
    isApprove ||
    (masterStatus &&
      masterStatus !== EXPENSE_STATUS.DRAFT &&
      masterStatus !== EXPENSE_STATUS.REJECTED);

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (isApprove) return '승인 화면에서는 추가할 수 없습니다.';
      if ((id === VENDOR_GRID_ID || id === CONTRACT_GRID_ID) && !selectedUidRef.current) {
        return '원인행위를 선택하거나 신규 추가하세요.';
      }
      return null;
    },
    guardDelete: () => (isApprove ? '승인 화면에서는 삭제할 수 없습니다.' : null),
  });

  const dimsFromSearch = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('searchFiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const enrichListRow = useCallback(
    (row) => ({
      ...row,
      statusNm: expenseStatusLabel(row.status),
      obligationTypeNm: obligationTypeLabel(row.obligationType),
    }),
    []
  );

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '번호', field: 'obligationNo', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 110 }),
      hiCol.roNumberColumn({ header: '금액', field: 'obligationAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '일자', field: 'obligationDate', size: 100, align: 'center' }),
    ],
    []
  );

  const vendorColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '거래처코드', field: 'vendorCode', size: 100, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '거래처명', field: 'vendorName', size: 140, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '사업자번호', field: 'businessNo', size: 110, editable: !masterReadonly }),
      hiCol.edNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right', editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 140, editable: !masterReadonly }),
    ],
    [masterReadonly]
  );

  const contractColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '계약번호', field: 'contractNo', size: 100, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '계약명', field: 'contractName', size: 140, editable: !masterReadonly }),
      hiCol.edDateColumn({ header: '계약일', field: 'contractDate', size: 110, editable: !masterReadonly }),
      hiCol.edNumberColumn({ header: '계약금액', field: 'contractAmt', size: 100, align: 'right', editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '거래처', field: 'vendorName', size: 120, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 120, editable: !masterReadonly }),
    ],
    [masterReadonly]
  );

  const vendorGrid = useHiGridHook({
    gridRef: vendorRef,
    autoSearch: false,
    enableContextMenu: !isApprove && !masterReadonly,
    confirmCheck: !isApprove && !masterReadonly,
    defaultRow: emptyObligationVendorRow(),
  });

  const contractGrid = useHiGridHook({
    gridRef: contractRef,
    autoSearch: false,
    enableContextMenu: !isApprove && !masterReadonly,
    confirmCheck: !isApprove && !masterReadonly,
    defaultRow: emptyContractRow(),
  });

  const loadDetails = useCallback(
    (uid) => {
      const vendors = (bundleVendors || []).filter((v) => v.obligationUid === uid && v.mod !== 'D');
      const contracts = (bundleContracts || []).filter((c) => c.obligationUid === uid && c.mod !== 'D');
      vendorRef.current?.setData?.(vendors);
      contractRef.current?.setData?.(contracts);
      setContractCount(contracts.length);
    },
    [bundleVendors, bundleContracts]
  );

  const loadMasterToForm = useCallback(
    (row) => {
      const searchKeep = pickSearchValues(getValues());
      if (!row) {
        reset({
          ...searchKeep,
          ...emptyMasterForm({ fiscalYear: searchKeep.searchFiscalYear }),
        });
        vendorRef.current?.setData?.([]);
        contractRef.current?.setData?.([]);
        setContractCount(0);
        return;
      }
      reset({
        ...searchKeep,
        ...row,
        statusNm: expenseStatusLabel(row.status),
      });
      loadDetails(row.obligationUid);
    },
    [reset, getValues, loadDetails]
  );

  const buildSearchFilter = useCallback(() => {
    const v = getValues();
    return {
      status: String(v.searchStatus || '').trim() || undefined,
      deptCode: String(v.searchDeptCode || '').trim() || undefined,
      deptName: String(v.searchDeptName || '').trim() || undefined,
      projectCode: String(v.searchProjectCode || '').trim() || undefined,
      projectName: String(v.searchProjectName || '').trim() || undefined,
      subjectCode: String(v.searchSubjectCode || '').trim() || undefined,
      subjectName: String(v.searchSubjectName || '').trim() || undefined,
      dateFrom: String(v.searchDateFrom || '').trim() || undefined,
      dateTo: String(v.searchDateTo || '').trim() || undefined,
      remark: String(v.searchRemark || '').trim() || undefined,
    };
  }, [getValues]);

  const handleSearch = useCallback(async () => {
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const bundle = await loadObligationBundle(dims);
    const filter = buildSearchFilter();
    let masters;
    if (isApprove) {
      if (isConfirmMode) {
        // CONFIRM 모드: 확정/승인 건 조회용
        masters = filterObligationsForRegister(bundle.masters, {
          ...filter,
          status: undefined,
        }).filter(
          (m) => m.status === EXPENSE_STATUS.CONFIRMED || m.status === EXPENSE_STATUS.APPROVED
        );
        if (filter.status) masters = masters.filter((m) => m.status === filter.status);
      } else {
        masters = filterObligationsForApprove(bundle.masters, filter);
      }
    } else {
      masters = filterObligationsForRegister(bundle.masters, filter);
    }
    masters = masters.map(enrichListRow);
    setBundleVendors(bundle.vendors);
    setBundleContracts(bundle.contracts);
    listRef.current?.setData?.(masters);
    setListCount(masters.length);
    const first = masters[0];
    setSelectedUid(first?.obligationUid || '');
    loadMasterToForm(first);
  }, [
    dimsFromSearch,
    buildSearchFilter,
    isApprove,
    isConfirmMode,
    enrichListRow,
    loadMasterToForm,
  ]);

  useEffect(() => {
    reset({
      ...searchDefaults,
      ...emptyMasterForm({ fiscalYear: searchDefaults.searchFiscalYear }),
    });
  }, [reset, searchDefaults]);

  const onListSelect = useCallback(
    (row) => {
      setSelectedUid(row?.obligationUid || '');
      loadMasterToForm(row);
    },
    [loadMasterToForm]
  );

  const listGrid = useHiGridHook({
    gridRef: listRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => onListSelect(data);
    listGrid.onSelectRow = ({ data }) => onListSelect(data);
  }, [listGrid, onListSelect]);

  const collectVendors = useCallback(() => {
    const uid = selectedUidRef.current;
    return (vendorRef.current?.getData?.() || [])
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        obligationUid: uid,
        detailUid: r.detailUid || createObligationDetailUid(),
      }));
  }, []);

  const collectContracts = useCallback(() => {
    const uid = selectedUidRef.current;
    return (contractRef.current?.getData?.() || [])
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        obligationUid: uid,
        contractUid: r.contractUid || createContractUid(),
      }));
  }, []);

  const handleNew = useCallback(async () => {
    if (isApprove) return;
    const uid = createObligationUid();
    const dims = dimsFromSearch();
    const bundle = await loadObligationBundle(dims);
    const row = enrichListRow(
      emptyObligationMaster({
        obligationUid: uid,
        obligationNo: nextObligationNo(bundle.masters),
        fiscalYear: dims.fiscalYear,
        obligationDate: todayYmd(),
        status: EXPENSE_STATUS.DRAFT,
        registerDate: todayYmd(),
        mod: 'A',
      })
    );
    setSelectedUid(uid);
    reset({ ...pickSearchValues(getValues()), ...row });
    vendorRef.current?.setData?.([]);
    contractRef.current?.setData?.([]);
    setContractCount(0);
  }, [isApprove, dimsFromSearch, enrichListRow, reset, getValues]);

  const handleSave = useCallback(async () => {
    if (isApprove) {
      showAlert('승인 화면에서는 저장할 수 없습니다.');
      return;
    }
    const dims = dimsFromSearch();
    let uid = selectedUidRef.current;
    if (!uid) {
      uid = createObligationUid();
      setSelectedUid(uid);
      selectedUidRef.current = uid;
    }
    const form = getValues();
    const bundle = await loadObligationBundle(dims);
    let masters = [...(bundle.masters || [])];
    const idx = masters.findIndex((m) => m.obligationUid === uid);
    const masterRow = {
      ...emptyObligationMaster(),
      ...form,
      obligationUid: uid,
      obligationNo: form.obligationNo || nextObligationNo(masters),
      fiscalYear: dims.fiscalYear,
      registerDate: form.registerDate || todayYmd(),
      status: form.status || EXPENSE_STATUS.DRAFT,
      mod: idx >= 0 ? 'U' : 'A',
    };
    if (idx >= 0) masters[idx] = masterRow;
    else masters.push(masterRow);

    let vendors = (bundle.vendors || []).filter((v) => v.obligationUid !== uid);
    let contracts = (bundle.contracts || []).filter((c) => c.obligationUid !== uid);
    vendors = [...vendors, ...collectVendors()];
    contracts = [...contracts, ...collectContracts()];

    const saved = await saveObligationBundle(dims, masters, vendors, contracts);
    setBundleVendors(saved.vendors);
    setBundleContracts(saved.contracts);
    listRef.current?.setData?.(saved.masters.filter((m) => m.mod !== 'D').map(enrichListRow));
    showAlert('저장했습니다.', 'success');
  }, [isApprove, dimsFromSearch, getValues, collectVendors, collectContracts, enrichListRow]);

  const handleConfirm = useCallback(async () => {
    if (isApprove || !isConfirmMode) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('확정할 원인행위를 선택하세요.');
      return;
    }
    const ok = await showConfirm('원인행위를 확정할까요?');
    if (!ok) return;
    await handleSave();
    const dims = dimsFromSearch();
    const bundle = await loadObligationBundle(dims);
    const result = confirmObligation(bundle.masters, bundle.vendors, bundle.contracts, uid);
    await saveObligationBundle(dims, result.masters, result.vendors, result.contracts);
    await handleSearch();
    showAlert('확정했습니다.', 'success');
  }, [isApprove, isConfirmMode, handleSave, dimsFromSearch, handleSearch]);

  const handleRequest = useCallback(async () => {
    if (isApprove || isConfirmMode) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인요청할 원인행위를 선택하세요.');
      return;
    }
    const ok = await showConfirm('승인요청할까요?');
    if (!ok) return;
    await handleSave();
    const dims = dimsFromSearch();
    const bundle = await loadObligationBundle(dims);
    const result = requestObligationApproval(bundle.masters, bundle.vendors, bundle.contracts, uid);
    await saveObligationBundle(dims, result.masters, result.vendors, result.contracts);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [isApprove, isConfirmMode, handleSave, dimsFromSearch, handleSearch]);

  const handleApprove = useCallback(async () => {
    if (!isApprove || isConfirmMode) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인할 건을 선택하세요.');
      return;
    }
    const ok = await showConfirm('승인할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadObligationBundle(dims);
    const result = approveObligation(bundle.masters, bundle.vendors, bundle.contracts, uid);
    await saveObligationBundle(dims, result.masters, result.vendors, result.contracts);
    await handleSearch();
    showAlert('승인했습니다.', 'success');
  }, [isApprove, isConfirmMode, dimsFromSearch, handleSearch]);

  const handleReject = useCallback(async () => {
    if (!isApprove || isConfirmMode) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('반려할 건을 선택하세요.');
      return;
    }
    const ok = await showConfirm('반려할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadObligationBundle(dims);
    const result = rejectObligation(bundle.masters, bundle.vendors, bundle.contracts, uid);
    await saveObligationBundle(dims, result.masters, result.vendors, result.contracts);
    await handleSearch();
    showAlert('반려했습니다.', 'success');
  }, [isApprove, isConfirmMode, dimsFromSearch, handleSearch]);

  const dispatchVendorAdd = useCallback(async () => {
    if (isApprove || masterReadonly || !selectedUidRef.current) return;
    vendorRef.current?.setData?.([
      ...(vendorRef.current?.getData?.() || []),
      emptyObligationVendorRow({
        detailUid: createObligationDetailUid(),
        obligationUid: selectedUidRef.current,
        mod: 'A',
      }),
    ]);
  }, [isApprove, masterReadonly]);

  const dispatchContractAdd = useCallback(async () => {
    if (isApprove || masterReadonly || !selectedUidRef.current) return;
    const next = [
      ...(contractRef.current?.getData?.() || []),
      emptyContractRow({
        contractUid: createContractUid(),
        obligationUid: selectedUidRef.current,
        mod: 'A',
      }),
    ];
    contractRef.current?.setData?.(next);
    setContractCount(next.filter((r) => r.mod !== 'D').length);
  }, [isApprove, masterReadonly]);

  /** 원인행위 계약 그리드 → 계약관리 대장 생성 + N:M 링크 */
  const handleCreateContractLedger = useCallback(async () => {
    if (isApprove) {
      showAlert('승인 화면에서는 계약대장을 생성할 수 없습니다.');
      return;
    }
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('원인행위를 선택하거나 저장한 뒤 계약대장을 생성하세요.');
      return;
    }
    const rows = (contractRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
    if (!rows.length) {
      showAlert('생성할 계약 정보가 없습니다. 계약 그리드에 행을 추가·저장하세요.');
      return;
    }
    const form = getValues();
    const ok = await showConfirm(
      `원인행위 ${form.obligationNo || ''}의 계약 ${rows.length}건으로 계약대장을 생성·연결할까요?`
    );
    if (!ok) return;

    const dims = dimsFromSearch();
    // 원인행위 먼저 저장해 UID·번호 확정
    await handleSave();
    const obl = {
      obligationUid: uid,
      obligationNo: form.obligationNo || getValues('obligationNo'),
      obligationType: form.obligationType,
      obligationAmt: form.obligationAmt,
      remark: form.remark,
      deptCode: form.deptCode,
      deptName: form.deptName,
      projectCode: form.projectCode,
      projectName: form.projectName,
      subjectCode: form.subjectCode,
      subjectName: form.subjectName,
      fundCode: form.fundCode,
      fundName: form.fundName,
    };
    const result = await createContractsFromObligation({
      tenantCode,
      fiscalYear: dims.fiscalYear,
      obligation: obl,
      contractRows: rows,
    });
    showAlert(
      `계약대장 생성 ${result.created}건 · 원인행위 연결 ${result.linked}건.\n계약관리 > 계약대장등록에서 확인하세요.`,
      'success'
    );
  }, [isApprove, getValues, dimsFromSearch, handleSave, tenantCode]);

  /** 계약대장 → 원인행위 N:M 불러오기 */
  const openContractLookup = useCallback(async () => {
    if (isApprove || masterReadonly) return;
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    if (!selectedUidRef.current) {
      showAlert('원인행위를 선택하거나 추가한 뒤 계약을 불러오세요.');
      return;
    }
    await ensureContractSeed(tenantCode, dims.fiscalYear);
    const b = await loadContractBundle(tenantCode, dims.fiscalYear);
    setContractLookupRows(filterContracts(b.contracts, { primaryOnly: true }));
    setContractLookupOpen(true);
  }, [isApprove, masterReadonly, dimsFromSearch, tenantCode]);

  const contractLookupConfig = useMemo(() => {
    if (!contractLookupOpen) {
      return { title: '계약 불러오기', rows: [], columns: [] };
    }
    const rows = (contractLookupRows || [])
      .filter((c) => c.status === 'SIGNED' || c.status === 'ACTIVE' || c.status === 'COMPLETED')
      .map((c) => ({
        ...enrichContractRow(c),
        id: c.contractUid,
      }));
    return {
      title: '계약대장 불러오기 (N:M · 물품·용역)',
      rows,
      columns: [
        hiCol.roTextColumn({ header: '계약번호', field: 'contractNo', size: 110 }),
        hiCol.roTextColumn({ header: '계약명', field: 'contractName', size: 150 }),
        hiCol.roTextColumn({ header: '목적물', field: 'objectTypeNm', size: 70, align: 'center' }),
        hiCol.roTextColumn({ header: '채널', field: 'primaryChannelNm', size: 100 }),
        hiCol.roTextColumn({ header: '상대방', field: 'vendorName', size: 120 }),
        hiCol.roNumberColumn({ header: '금액', field: 'contractAmt', size: 100, align: 'right' }),
        hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 70, align: 'center' }),
      ],
      enableSearch: true,
      searchConfig: {
        mode: 'unified',
        keywordFields: ['contractNo', 'contractName', 'vendorName', 'remark'],
      },
    };
  }, [contractLookupOpen, contractLookupRows]);

  const handleContractLookupSelect = useCallback(
    async (payload) => {
      const selected = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      if (!selected.length) {
        showAlert('계약을 선택하세요.');
        return false;
      }
      const uid = selectedUidRef.current;
      const form = getValues();
      const dims = dimsFromSearch();
      const result = await linkContractsToObligation({
        tenantCode,
        fiscalYear: dims.fiscalYear,
        obligation: {
          obligationUid: uid,
          obligationNo: form.obligationNo,
        },
        contractsToLink: selected,
      });
      // 원인행위 계약 그리드에 스냅샷 병합 (중복 contractUid 제외)
      const existing = (contractRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
      const existUids = new Set(existing.map((r) => String(r.contractUid || '')));
      const existNos = new Set(existing.map((r) => String(r.contractNo || '').trim()).filter(Boolean));
      const merged = [...existing];
      (result.snapshotRows || []).forEach((snap) => {
        if (snap.contractUid && existUids.has(String(snap.contractUid))) return;
        if (snap.contractNo && existNos.has(String(snap.contractNo).trim())) return;
        merged.push({
          ...emptyContractRow(snap),
          contractUid: snap.contractUid || createContractUid(),
          obligationUid: uid,
          mod: 'A',
        });
      });
      contractRef.current?.setData?.(merged);
      setContractCount(merged.length);
      showAlert(`계약 ${result.linked}건 연결 · 그리드 ${merged.length}건 반영. 저장(F10)하세요.`, 'success');
      return true;
    },
    [getValues, dimsFromSearch, tenantCode]
  );

  const [proposalTabRows, setProposalTabRows] = useState([]);

  useEffect(() => {
    let live = true;
    (async () => {
      const dims = dimsFromSearch();
      const propBundle = await loadProposalBundle(dims);
      const propRows = (propBundle.masters || [])
        .filter(
          (m) =>
            m.mod !== 'D' &&
            (m.status === EXPENSE_STATUS.APPROVED || m.status === EXPENSE_STATUS.CONFIRMED)
        )
        .map((m) => ({
          ...m,
          id: m.proposalUid,
          statusNm: expenseStatusLabel(m.status),
        }));
      if (live) setProposalTabRows(propRows);
    })();
    return () => {
      live = false;
    };
  }, [dimsFromSearch, tenantCode]);

  const loadDialogConfig = useMemo(() => {
    const dims = dimsFromSearch();
    const env = getExpenseLoadEnv(tenantCode);
    const balanceRows = loadExpenseBalanceRows(tenantCode, dims.fiscalYear);
    const upstreamTab = {
      id: 'proposal',
      label: '품의',
      hint: '승인·확정된 품의를 불러옵니다. 없으면 현액만으로 원인행위를 등록할 수 있습니다.',
      columns: [
        hiCol.roTextColumn({ header: '번호', field: 'proposalNo', size: 80, align: 'center' }),
        hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
        hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 110 }),
        hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 110 }),
        hiCol.roNumberColumn({ header: '금액', field: 'proposalAmt', size: 100 }),
        hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      ],
      rows: proposalTabRows,
    };
    const built = buildLoadDialogTabs({
      tabsMode: env.obligationTabs,
      defaultTab: env.obligationDefaultTab,
      upstreamTab,
      balanceRows,
      bothTitle: '원인행위 불러오기',
      balanceOnlyTitle: '현액 조회 (지출)',
    });
    return { ...built, enableSearch: true, searchConfig: BALANCE_SEARCH_CONFIG };
  }, [dimsFromSearch, tenantCode, proposalTabRows]);

  const applyProposalToForm = useCallback(
    (p) => {
      if (!p) return;
      setValue('proposalUid', p.proposalUid || '');
      setValue('proposalNo', p.proposalNo || '');
      setValue('balanceUid', p.balanceUid || '');
      setValue('deptCode', p.deptCode || '');
      setValue('deptName', p.deptName || '');
      setValue('projectCode', p.projectCode || '');
      setValue('projectName', p.projectName || '');
      setValue('subjectCode', p.subjectCode || '');
      setValue('subjectName', p.subjectName || '');
      setValue('fundCode', p.fundCode || '');
      setValue('fundName', p.fundName || '');
      if (p.proposalAmt != null) setValue('obligationAmt', p.proposalAmt);
    },
    [setValue]
  );

  const handleLoadSelect = useCallback(
    async (row) => {
      const r = Array.isArray(row) ? row[0] : row;
      if (!r) return false;
      const tab = r._lookupTab || '';
      const isProposal = tab === 'proposal' || (!tab && r.proposalUid && r.proposalNo);

      if (isProposal) {
        applyProposalToForm(r);
        return true;
      }

      const dims = dimsFromSearch();
      const ok = await confirmUpstreamDupIfNeeded({
        tenantCode,
        dims,
        balanceRow: r,
        upstreamKind: 'proposal',
        showConfirm,
        showAlert,
      });
      if (!ok) return false;
      applyBalanceFields(setValue, r);
      setValue('proposalUid', '');
      setValue('proposalNo', '');
      return true;
    },
    [applyProposalToForm, dimsFromSearch, tenantCode, setValue]
  );

  const searchLookupConfig = useMemo(() => {
    const fy = String(searchFiscalYearWatch || getCurrentFiscalYear()).trim();
    if (searchLookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenantCode), { useYn: 'Y' });
      return {
        title: '부서 조회 (검색)',
        rows: rows.map((r) => ({ ...r, id: r.deptUid || r.deptCode })),
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['deptCode', 'deptName'] },
      };
    }
    if (searchLookup.type === 'project') {
      const loaded = loadOrSeedProjects({ tenantCode, fiscalYear: fy }) || [];
      const rows = (Array.isArray(loaded) ? loaded : []).filter((r) => r.useYn !== 'N');
      return {
        title: '사업 조회 (검색)',
        rows: rows.map((r) => ({ ...r, id: r.projectUid || r.projectCode })),
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['projectCode', 'projectName'] },
      };
    }
    if (searchLookup.type === 'subject') {
      const rows = (loadSubjectsByFilter({ tenantCode, fiscalYear: fy, inoutDiv: '지출' }) || []).filter(
        (r) => r.useYn !== 'N' && r.mod !== 'D'
      );
      return {
        title: '과목 조회 (검색)',
        rows: rows.map((r) => ({
          ...r,
          id: r.subjectUid || r.subjectCode,
        })),
        columns: [
          hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 110 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['subjectDispCode', 'subjectCode', 'subjectName'],
        },
      };
    }
    return { title: '', rows: [], columns: [], enableSearch: false };
  }, [searchLookup.type, tenantCode, searchFiscalYearWatch]);

  const applySearchLookup = useCallback(
    (payload) => {
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      const axis = {
        dept: {
          keys: ['searchDeptCode', 'searchDeptName', 'searchDeptDisp'],
          code: (r) => r.deptCode,
          name: (r) => r.deptName,
        },
        project: {
          keys: ['searchProjectCode', 'searchProjectName', 'searchProjectDisp'],
          code: (r) => r.projectCode,
          name: (r) => r.projectName,
        },
        subject: {
          keys: ['searchSubjectCode', 'searchSubjectName', 'searchSubjectDisp'],
          code: (r) => r.subjectDispCode || r.subjectCode,
          name: (r) => r.subjectName,
        },
      }[searchLookup.type];
      if (!axis) return true;

      const [codeKey, nameKey, dispKey] = axis.keys;
      if (!rows.length) {
        setValue(codeKey, '');
        setValue(nameKey, '');
        setValue(dispKey, '');
        return true;
      }

      const codes = rows.map((r) => String(axis.code(r) || '').trim()).filter(Boolean);
      const names = rows.map((r) => String(axis.name(r) || '').trim()).filter(Boolean);
      setValue(codeKey, codes.join(','));
      setValue(nameKey, names.join(','));
      setValue(dispKey, summarizeSelection(names));
      return true;
    },
    [searchLookup.type, setValue]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'searchFiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          parentWidth: SEARCH_SLOT_W[0],
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchStatus',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...EXPENSE_STATUS_OPTIONS],
          parentWidth: SEARCH_SLOT_W[1],
          width: '140px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchObligationDateRange',
          label: '행위일',
          type: 'dateRange',
          startFieldName: 'searchDateFrom',
          endFieldName: 'searchDateTo',
          parentWidth: SEARCH_SLOT_W[2],
          width: SEARCH_DATE_W,
          labelWidth: SEARCH_LABEL_W,
          fiscalYear: searchFiscalYearWatch || getCurrentFiscalYear(),
          presetSet: 'inquiry',
        },
      ],
      [
        {
          name: 'searchDeptDisp',
          label: '부서',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[0],
          width: '160px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '부서 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'dept' }),
          title: selectionTooltip(searchDeptNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchProjectDisp',
          label: '사업',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[1],
          width: '210px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '사업 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'project' }),
          title: selectionTooltip(searchProjectNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchSubjectDisp',
          label: '과목',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[2],
          width: '210px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '과목 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'subject' }),
          title: selectionTooltip(searchSubjectNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchRemark',
          label: '적요',
          type: 'text',
          width: '320px',
          labelWidth: SEARCH_LABEL_W,
          placeholder: '부분 일치',
        },
      ],
    ],
    [searchFiscalYearWatch, searchDeptNameWatch, searchProjectNameWatch, searchSubjectNameWatch]
  );

  const masterFields = useMemo(
    () => [
      [
        {
          name: 'obligationNo',
          label: '번호',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[0],
          width: '112px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'deptName',
          label: '부서',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[1],
          width: '150px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'projectName',
          label: '사업',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[2],
          width: '210px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'subjectName',
          label: '과목',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[3],
          width: '210px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'fundName',
          label: '재원',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[4],
          width: '120px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'proposalNo',
          label: '품의번호',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[5],
          width: '112px',
          labelWidth: MASTER_LABEL_W,
        },
      ],
      [
        {
          name: 'obligationAmt',
          label: '금액',
          type: 'number',
          parentWidth: MASTER_SLOT_W[0],
          width: '130px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'obligationDate',
          label: '행위일',
          type: 'date',
          parentWidth: MASTER_SLOT_W[1],
          width: '140px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'obligationType',
          label: '유형',
          type: 'select',
          options: OBLIGATION_TYPE_OPTIONS,
          parentWidth: MASTER_SLOT_W[2],
          width: '150px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'statusNm',
          label: '상태',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[3],
          width: '90px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'remark',
          label: '적요',
          type: 'text',
          parentWidth: MASTER_SLOT_W_REMARK,
          width: '330px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
      ],
    ],
    [masterReadonly]
  );

  const actionButtons = useMemo(() => {
    const base = [{ id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' }];
    if (isApprove) return base;
    return [
      ...base,
      { id: 'new', label: '추가', icon: '➕', onClick: handleNew, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel}` : '삭제 (F9)',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    ];
  }, [isApprove, handleSearch, handleNew, handleActiveDelete, handleSave, activeLabel]);

  const extraButtons = useMemo(() => {
    if (isApprove) {
      if (isConfirmMode) return [];
      return [
        { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
        { id: 'reject', label: '반려', onClick: handleReject, variant: 'danger' },
      ];
    }
    if (isConfirmMode) {
      return [{ id: 'confirm', label: '확정', onClick: handleConfirm, variant: 'primary' }];
    }
    return [{ id: 'request', label: '승인요청', onClick: handleRequest }];
  }, [
    isApprove,
    isConfirmMode,
    handleConfirm,
    handleRequest,
    handleApprove,
    handleReject,
  ]);

  const approveHint = isApprove && isConfirmMode
    ? '확정 모드 — 확정/승인 건 조회용 (승인/반려 없음)'
    : isApprove
      ? '승인요청 건 선택 후 승인/반려'
      : isConfirmMode
        ? '저장 후 [확정] — 승인 단계 없음'
        : '저장 후 [승인요청]';

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack {...scrollbarHoverProps}>
          <CollapsibleSearchPanel
            storageKey={`expense-obligation-${screenMode}`}
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            {approveHint}
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
            {!isApprove ? ' · 계약·목록은 펼치면 아래로 늘어남 · Alt+F2 그리드 전환' : ''}
          </Hint>

          <Panel>
            <MasterToolbar>
              <strong style={{ fontSize: theme.typography.fontSize.sm }}>원인행위 마스터</strong>
              <BalanceBtn
                type="button"
                onClick={() => setBalanceLookupOpen(true)}
                disabled={isApprove || masterReadonly}
                title={
                  isApprove
                    ? '승인 화면에서는 불러올 수 없습니다.'
                    : masterReadonly
                      ? '작성중·반려 건만 불러올 수 있습니다. [추가]로 신규 작성하세요.'
                      : '품의·현액 불러오기'
                }
              >
                불러오기 ···
              </BalanceBtn>
            </MasterToolbar>
            <FormArea dense fields={masterFields} />
          </Panel>

          {/* 남는 공간은 차지하되(grow), 계약·목록을 펼쳐도 줄어들지 않음(shrink 0) */}
          <Panel $flex="1 0 auto" $height={VENDOR_PANEL_H}>
            <ActiveGridScope
              gridId={VENDOR_GRID_ID}
              label="거래처"
              order={10}
              onAdd={dispatchVendorAdd}
              onDelete={async () => vendorGrid.handleDelete?.()}
              canAdd={!isApprove && !masterReadonly}
              canDelete={!isApprove && !masterReadonly}
            >
              <ActiveGridHeader gridId={VENDOR_GRID_ID}>거래처</ActiveGridHeader>
              <GridBody>
                <HiGridComponent
                  ref={vendorRef}
                  columns={vendorColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableContextMenu={!isApprove && !masterReadonly}
                  enableRowEditMenu={!isApprove && !masterReadonly}
                  enableCut={!isApprove && !masterReadonly}
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="원인행위거래처"
                  gridId={`expense-obligation-vendor-${screenMode}`}
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === VENDOR_GRID_ID}
                  {...(isApprove || masterReadonly ? {} : vendorGrid.contextMenuProps || {})}
                />
              </GridBody>
            </ActiveGridScope>
          </Panel>

          <CollapsibleSectionPanel
            storageKey={`expense-obligation-contract-${screenMode}`}
            prefsUserId={prefsUserId}
            title="계약"
            summary={contractCount ? `${contractCount}건` : '선택적으로 입력'}
            defaultCollapsed
            expandedHeight={CONTRACT_PANEL_H}
            expandedMaxHeight="none"
            headerExtra={
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <BalanceBtn
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openContractLookup();
                  }}
                  disabled={isApprove || masterReadonly}
                  title={
                    isApprove || masterReadonly
                      ? '작성중·반려 건만 계약을 불러올 수 있습니다.'
                      : '계약관리 대장에서 계약 불러오기 (N:M)'
                  }
                >
                  계약불러오기 ···
                </BalanceBtn>
                <BalanceBtn
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateContractLedger();
                  }}
                  disabled={isApprove}
                  title={
                    isApprove
                      ? '승인 화면에서는 생성할 수 없습니다.'
                      : '원인행위 계약정보 → 계약관리 계약대장 생성·연결'
                  }
                >
                  계약대장생성
                </BalanceBtn>
              </span>
            }
          >
            <ActiveGridScope
              gridId={CONTRACT_GRID_ID}
              label="계약"
              order={20}
              onAdd={dispatchContractAdd}
              onDelete={async () => {
                await contractGrid.handleDelete?.();
                const n = (contractRef.current?.getData?.() || []).filter((r) => r.mod !== 'D').length;
                setContractCount(n);
              }}
              canAdd={!isApprove && !masterReadonly}
              canDelete={!isApprove && !masterReadonly}
            >
              <GridBody>
                <HiGridComponent
                  ref={contractRef}
                  columns={contractColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableContextMenu={!isApprove && !masterReadonly}
                  enableRowEditMenu={!isApprove && !masterReadonly}
                  enableCut={!isApprove && !masterReadonly}
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="원인행위계약"
                  gridId={`expense-obligation-contract-${screenMode}`}
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === CONTRACT_GRID_ID}
                  {...(isApprove || masterReadonly ? {} : contractGrid.contextMenuProps || {})}
                />
              </GridBody>
            </ActiveGridScope>
          </CollapsibleSectionPanel>

          <CollapsibleSectionPanel
            storageKey={`expense-obligation-list-${screenMode}`}
            prefsUserId={prefsUserId}
            title="원인행위 목록"
            summary={listCount ? `${listCount}건` : '조회 후 표시'}
            defaultCollapsed
            expandedHeight={LIST_PANEL_H}
            expandedMaxHeight="none"
          >
            <GridBody>
              <HiGridComponent
                ref={listRef}
                columns={listColumns}
                showRowNumber
                rowNumberSize={48}
                enableColumnFilters
                enableHeaderMenu
                enableQuickExport
                exportFileName="원인행위목록"
                gridId={`expense-obligation-list-${screenMode}`}
                prefsUserId={prefsUserId}
              />
            </GridBody>
          </CollapsibleSectionPanel>
        </Stack>
      </FormProvider>

      <CodeLookupDialog
        open={balanceLookupOpen}
        title={loadDialogConfig.title}
        tabs={loadDialogConfig.tabs}
        columns={loadDialogConfig.columns}
        rows={loadDialogConfig.rows}
        defaultTabId={loadDialogConfig.defaultTabId}
        enableSearch={loadDialogConfig.enableSearch}
        searchConfig={loadDialogConfig.searchConfig}
        prefsUserId={prefsUserId}
        sizeKey="codeLookup-expense-obligation-load"
        dialogWidth="860px"
        onSelect={handleLoadSelect}
        onClose={() => setBalanceLookupOpen(false)}
      />

      {!isApprove && (
        <CodeLookupDialog
          open={contractLookupOpen}
          title={contractLookupConfig.title}
          columns={contractLookupConfig.columns}
          rows={contractLookupConfig.rows}
          multiSelect
          enableSearch={contractLookupConfig.enableSearch}
          searchConfig={contractLookupConfig.searchConfig}
          prefsUserId={prefsUserId}
          sizeKey="codeLookup-expense-obligation-contract"
          dialogWidth="900px"
          onSelect={handleContractLookupSelect}
          onClose={() => setContractLookupOpen(false)}
        />
      )}

      <CodeLookupDialog
        open={searchLookup.open}
        title={searchLookupConfig.title}
        columns={searchLookupConfig.columns}
        rows={searchLookupConfig.rows}
        enableSearch={searchLookupConfig.enableSearch}
        searchConfig={searchLookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey={`codeLookup-obligation-search-${searchLookup.type || 'x'}`}
        onSelect={applySearchLookup}
        onClose={() => setSearchLookup({ open: false, type: '' })}
      />
    </PageContainer>
  );
}

export default function ExpenseObligationWorkspace(props) {
  return (
    <ActiveGridProvider>
      <ExpenseObligationWorkspaceInner {...props} />
    </ActiveGridProvider>
  );
}
